import axios from 'axios';
import Cookies from 'js-cookie';

import { buildEndpoint, getSubdomain } from 'helpers/apiHelper';

const TOKEN_COOKIE = '__kopek';
const REFRESH_COOKIE = '__kopek_r';

// the JWT itself only lasts 60 minutes — both cookies outlive it so that an
// expired token can still be read, rejected with a 403, and refreshed
const COOKIE_EXPIRY_IN_DAYS = 60;

const subdomain = getSubdomain(window.location.href);

const baseClient = axios.create({
    baseURL: buildEndpoint(),
    headers: { 'X-Subdomain': subdomain }
});

export const getToken = () => Cookies.get(TOKEN_COOKIE);

export const setTokens = ({ token, refresh }) => {
    Cookies.set(TOKEN_COOKIE, token, { expires: COOKIE_EXPIRY_IN_DAYS });
    Cookies.set(REFRESH_COOKIE, refresh, { expires: COOKIE_EXPIRY_IN_DAYS });
}

export const clearTokens = () => {
    Cookies.remove(TOKEN_COOKIE);
    Cookies.remove(REFRESH_COOKIE);
}

// lets AppWrapper log the user out when the refresh key is dead
let onRefreshFailed = () => {};

export const setOnRefreshFailed = (handler) => { onRefreshFailed = handler };

baseClient.interceptors.request.use((config) => {

    const token = getToken();

    if (token && ! config.skipAuth) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
});

let refreshPromise = null;

const refreshTokens = () => {

    // refresh keys rotate on use, so every waiting request shares one call
    if (! refreshPromise) {
        const refresh = Cookies.get(REFRESH_COOKIE);

        refreshPromise = (refresh ?
            baseClient.post('user/refresh', { refresh }, { skipAuth: true, skipRefresh: true }) :
            Promise.reject(new Error('No refresh key'))
        )
            .then((response) => {
                setTokens(response.data);
                return response.data.token;
            })
            .catch((error) => {
                clearTokens();
                onRefreshFailed();
                throw error;
            })
            .finally(() => { refreshPromise = null });
    }

    return refreshPromise;
}

baseClient.interceptors.response.use(
    (response) => response,
    async (error) => {

        const request = error.config;

        if (error.response?.status !== 403 || ! request || request.skipRefresh || request.retried) {
            throw error;
        }

        request.retried = true;

        const token = await refreshTokens();
        request.headers.Authorization = `Bearer ${token}`;

        return baseClient(request);
    }
);

export default baseClient;
